import { compareAsc } from 'date-fns';
import { useMemo } from 'react';

import { OrderModel, OrderStatusModel } from '@merchant-clients/order';

import { useLocale } from '@frontend/hooks';
import { usePersistentUiSettings } from '@frontend/utils';

import { OrdersSortField } from '../order-sort.model';
import { sortOrders } from './sort-orders-by-verification';

export const AllowedOrdersGroupByFieldModel = [
  'NONE',
  'STATUS',
  'DATE',
  'TIMESLOT',
] as const;
export type OrdersGroupByFieldModel =
  (typeof AllowedOrdersGroupByFieldModel)[number];

export const AllowedOrdersSortDirection = ['ASC', 'DESC'] as const;
export type OrdersSortDirection = (typeof AllowedOrdersSortDirection)[number];

export type GroupByStatusModel = Record<
  Exclude<OrderStatusModel, 'REJECTED' | 'REFUNDED'>,
  OrderModel[]
>;
export type GroupByDateModel = Record<string, OrderModel[]>;
export type GroupByNoneModel = Record<'ALL', OrderModel[]>;
export type GroupedOrdersModel =
  | GroupByStatusModel
  | GroupByDateModel
  | GroupByNoneModel;

export interface UseOrderGrouping {
  orders: OrderModel[];
}
export interface UseOrderGroupingReturn {
  groupedOrders: GroupedOrdersModel;
}

const getOrdersByGroup = (
  orders: OrderModel[],
  groupBy: Exclude<OrdersGroupByFieldModel, 'NONE' | 'TIMESLOT'>,
  includeStatuses: OrderStatusModel[],
) => {
  const statusGroups: GroupByStatusModel = includeStatuses
    .filter((s) => s !== 'REFUNDED' && s !== 'REJECTED')
    .reduce((acc, status) => {
      acc[status as Exclude<OrderStatusModel, 'REJECTED' | 'REFUNDED'>] = [];
      return acc;
    }, {} as GroupByStatusModel);
  const dateGroups: GroupByDateModel = {};

  if (groupBy === 'DATE' && (!orders || orders.length === 0)) {
    return {};
  }

  orders.forEach((order) => {
    const dateKey = order?.timeRequested || order.createdAt;

    if (groupBy === 'DATE') {
      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = [];
      }

      dateGroups[dateKey].push(order);
    }

    if (groupBy === 'STATUS') {
      if (!includeStatuses.some((s) => s === order.status)) {
        return;
      }
      let statusKey: OrderStatusModel;

      if (order.status === 'REJECTED' || order.status === 'REFUNDED') {
        statusKey = 'CLOSED';
      } else {
        statusKey = order.status;
      }
      statusGroups[statusKey].push(order);
    }
  });

  return groupBy === 'DATE' ? dateGroups : statusGroups;
};

// Sorts day groups by date ascending and formats the date keys to relative
// date strings, e.g. "Today (Monday)", "Tomorrow (Tuesday)".
const sortAndFormatGroupEntriesByDate = (
  groups: GroupByDateModel,
  formatRelativeDate: (date: Date) => string,
): GroupByDateModel => {
  const sortKeysByDate = Object.entries(groups).sort(([g1], [g2]) => {
    return compareAsc(new Date(g1), new Date(g2));
  });

  return sortKeysByDate.reduce((acc, [key, value]) => {
    const formattedKey = formatRelativeDate(new Date(key));

    if (!acc[formattedKey]) {
      acc[formattedKey] = [];
    }

    acc[formattedKey].push(...value);
    return acc;
  }, {} as GroupByDateModel);
};

const sortOrdersInGroups = <T extends GroupByStatusModel | GroupByDateModel>(
  groups: T,
  sortField: OrdersSortField,
): T => {
  return Object.fromEntries(
    Object.entries(groups).map(([key, groupOrders]) => [
      key,
      sortOrders(groupOrders as OrderModel[], sortField, 'ASC'),
    ]),
  ) as T;
};

export const useOrderGrouping = ({
  orders,
}: UseOrderGrouping): UseOrderGroupingReturn => {
  const { formatters } = useLocale();
  const {
    ordersPage: {
      groupBy,
      sortField,
      sortDirection,
      filter: { status },
    },
  } = usePersistentUiSettings();

  const groupedOrders = useMemo(() => {
    if (groupBy === 'NONE') {
      const noneGroup: GroupByNoneModel = {
        ALL: sortOrders(orders, sortField, sortDirection),
      };
      return noneGroup;
    }

    if (groupBy === 'DATE') {
      const groups = getOrdersByGroup(orders, groupBy, status);
      const formatted = sortAndFormatGroupEntriesByDate(
        groups,
        formatters.relativeDate,
      );
      return sortOrdersInGroups(formatted, sortField);
    }

    // TIMESLOT is rendered by OrderTimeslotList from a dedicated query.
    if (groupBy === 'TIMESLOT') {
      return { ALL: [] } satisfies GroupByNoneModel;
    }

    const groups = getOrdersByGroup(orders, groupBy, status);
    return sortOrdersInGroups(groups, sortField);
  }, [orders, groupBy, sortField, sortDirection, status, formatters]);

  return { groupedOrders };
};
